import { useRef } from 'react';
import { Text } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { VirtualScreenContent, VirtualScreenMount } from './conference-twin-data';

interface VirtualRoomScreenProps {
  mount: VirtualScreenMount;
  content: VirtualScreenContent;
  isSelected?: boolean;
  onSelect?: (roomId: string) => void;
}

const STATUS_STYLE: Record<VirtualScreenContent['status'], { label: string; color: string }> = {
  live: { label: 'LIVE NOW', color: '#34d399' },
  upcoming: { label: 'UP NEXT', color: '#7dd3fc' },
  ended: { label: 'SESSION ENDED', color: '#94a3b8' },
  idle: { label: 'ROOM AVAILABLE', color: '#cbd5e1' },
};

function formatScheduleWindow(content: VirtualScreenContent): string {
  if (content.startTime && content.endTime) return `${content.startTime} – ${content.endTime}`;
  if (content.startTime) return `Starts ${content.startTime}`;
  return content.nextSessionTitle ? `Up next: ${content.nextSessionTitle}` : 'Conference programme';
}

/**
 * Wall-mounted display geometry for a conference room. The content contract is
 * intentionally presentation-agnostic so slides, video, and Q&A can be added
 * without changing the scene mesh.
 */
export function VirtualRoomScreen({
  mount,
  content,
  isSelected = false,
  onSelect,
}: VirtualRoomScreenProps) {
  const status = STATUS_STYLE[content.status];
  const metadata = [formatScheduleWindow(content), content.presenter].filter(Boolean).join(' · ');
  const isInteractive = Boolean(onSelect);
  const displayRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const display = displayRef.current;
    if (!display) return;

    const material = display.material as THREE.MeshStandardMaterial;
    if (isSelected) {
      material.emissive.set('#38bdf8');
      material.emissiveIntensity = 0.7 + Math.sin(state.clock.elapsedTime * 4) * 0.22;
      return;
    }

    material.emissive.set('#1e293b');
    material.emissiveIntensity = content.status === 'live' ? 0.62 : 0.38;
  });

  const handleSelect = (event: ThreeEvent<MouseEvent>) => {
    if (!onSelect) return;
    event.stopPropagation();
    onSelect(content.roomId);
  };

  return (
    <group position={[mount.x, mount.y, mount.z]} rotation={[0, mount.rotationY, 0]}>
      {/* Rear plate keeps the panel visibly wall-mounted rather than floating. */}
      <mesh position={[0, 0, -0.18]} castShadow receiveShadow>
        <boxGeometry args={[mount.width + 0.38, mount.height + 0.38, 0.12]} />
        <meshStandardMaterial color="#0b1220" metalness={0.82} roughness={0.3} />
      </mesh>

      {/* Brushed aluminium bezel. */}
      <mesh
        position={[0, 0, -0.06]}
        castShadow
        receiveShadow
        onClick={handleSelect}
        onPointerOver={(event: ThreeEvent<PointerEvent>) => {
          if (!isInteractive) return;
          event.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          if (isInteractive) document.body.style.cursor = 'default';
        }}
      >
        <boxGeometry args={[mount.width + 0.22, mount.height + 0.22, 0.16]} />
        <meshStandardMaterial
          color={isSelected ? '#475569' : '#334155'}
          emissive={isSelected ? '#38bdf8' : '#000000'}
          emissiveIntensity={isSelected ? 0.32 : 0}
          metalness={0.9}
          roughness={0.25}
        />
      </mesh>

      {/* Dark glass display face with a restrained blue glow. */}
      <mesh ref={displayRef} position={[0, 0, 0.04]} onClick={handleSelect}>
        <boxGeometry args={[mount.width, mount.height, 0.035]} />
        <meshStandardMaterial
          color="#0f172a"
          emissive="#1e293b"
          emissiveIntensity={content.status === 'live' ? 0.62 : 0.38}
          metalness={0.28}
          roughness={0.2}
        />
      </mesh>

      <mesh position={[-mount.width / 2 + 0.34, mount.height / 2 - 0.29, 0.075]}>
        <circleGeometry args={[0.075, 16]} />
        <meshBasicMaterial color={status.color} />
      </mesh>
      <Text
        position={[-mount.width / 2 + 0.5, mount.height / 2 - 0.29, 0.08]}
        fontSize={0.16}
        color={status.color}
        anchorX="left"
        anchorY="middle"
      >
        {content.statusBadge ?? status.label}
      </Text>
      <Text
        position={[0, 0.14, 0.08]}
        fontSize={Math.min(0.34, mount.width * 0.095)}
        maxWidth={mount.width - 0.62}
        lineHeight={1.15}
        color="#f8fafc"
        anchorX="center"
        anchorY="middle"
        textAlign="center"
      >
        {content.sessionTitle}
      </Text>
      <Text
        position={[0, -mount.height / 2 + 0.32, 0.08]}
        fontSize={0.15}
        maxWidth={mount.width - 0.5}
        color="#cbd5e1"
        anchorX="center"
        anchorY="middle"
        textAlign="center"
      >
        {metadata}
      </Text>
    </group>
  );
}
